import { AppButton } from '@/ui/buttons';
import { FlexContainer } from '@/ui/styled-ui';
import React, { ReactNode } from 'react';
import styled, { css } from 'styled-components';

const hoverStyles = css`
  &:hover {
    background-color: var(--purple-dark);
    color: var(--white-main);
    transition: all 0.4s;
  }
`;

const StyledCard = styled(FlexContainer)<{ $addHoverEffect?: boolean }>`
  align-items: flex-start;
  justify-content: space-between;
  background-color: var(--white-main);
  flex-direction: column;
  border-radius: var(--medium-border-radius);
  padding: var(--default-padding);
  box-shadow: var(--box-shadow);
  width: 100%;
  max-width: 400px;
  gap: var(--xsmall-padding);

  .icon {
    border-radius: var(--small-border-radius);
    width: 55px;
    height: 55px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: var(--red-light-opacity);
    svg {
      color: var(--red-main);
    }
  }

  &.awareness {
    .icon {
      background-color: var(--blue-opacity);
      svg {
        color: var(--blue-dark);
      }
    }
  }

  &.phishing {
    .icon {
      background-color: var(--green-opacity);
      svg {
        color: var(--green-color);
      }
    }
  }

  &.user-management {
    .icon {
      background-color: var(--red-light-opacity);
      svg {
        color: var(--red-main);
      }
    }
  }

  &.custom-materials {
    max-width: 360px;
    padding-top: var(--big-padding);
    ul {
      margin-bottom: var(--default-padding);
    }
  }

  h3 {
    font-size: var(--body-font-size);
    line-height: var(--body-line-height);
    font-weight: 600;
    margin-top: var(--default-padding);
    margin-bottom: 0;
  }

  p,
  li {
    margin: 0;
    font-size: var(--small-font-size);
    line-height: var(--medium-line-height);
  }

  ul {
    padding-left: 0;
    margin-top: var(--xsmall-padding);
    margin-bottom: var(--medium-padding);
  }

  li {
    list-style: none;
    margin-left: var(--small-padding);
    margin-left: 0;

    &::before {
      font-size: var(--medium-font-size);
      content: '\u203A';
      margin-right: 8px;
      color: var(--text-color);
    }
  }

  .p-button {
    align-self: center;
    width: 100%;
  }

  ${(props) => props.$addHoverEffect && hoverStyles}
`;

type InfoCardProps = {
  icon: ReactNode;
  title: string;
  description: string;
  features: string[];
  actionText?: string;
  onClick?: () => void;
  className?: string;
  addHoverEffect?: boolean;
  action?: ReactNode;
};

export const InfoCard: React.FC<InfoCardProps> = ({
  icon,
  title,
  description,
  features,
  actionText,
  onClick,
  className,
  addHoverEffect = false,
  action,
}) => {
  return (
    <StyledCard className={className} $addHoverEffect={addHoverEffect}>
      <div className="icon">{icon}</div>
      <h3>{title}</h3>
      <p>{description}</p>
      <ul>
        {features.map((feature, index) => (
          <li key={index}>{feature}</li>
        ))}
      </ul>
      {action ? (
        action
      ) : (
        <AppButton severity="success" label={actionText} onClick={onClick} />
      )}
    </StyledCard>
  );
};
